import React from 'react';

interface PageHeaderProps {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}

// Page title row used at the top of every page (same styling as the warehouse pages).
export default function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 lg:mb-6">
      <div>
        <h1 className="text-xl lg:text-2xl font-extrabold text-ink-900">{title}</h1>
        {subtitle && <p className="text-sm text-ink-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">{actions}</div>}
    </div>
  );
}

export function KpiTile({
  label,
  value,
  hint,
  icon,
  tone = 'brand',
}: {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon: string;
  tone?: 'brand' | 'accent' | 'green' | 'red' | 'violet' | 'ink';
}) {
  const tones: Record<string, string> = {
    brand: 'bg-brand-50 text-brand-600',
    accent: 'bg-accent-50 text-accent-600',
    green: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    violet: 'bg-violet-50 text-violet-600',
    ink: 'bg-ink-100 text-ink-600',
  };
  return (
    <div className="bg-white rounded-lg p-4 border border-ink-200 shadow-soft">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-ink-500">{label}</p>
          <p className="text-xl lg:text-2xl font-extrabold text-ink-900 mt-1 truncate">{value}</p>
          {hint && <p className="text-xs text-ink-500 mt-1">{hint}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}>
          <i className={`fas ${icon}`}></i>
        </div>
      </div>
    </div>
  );
}

export function SectionCard({
  title,
  actions,
  children,
  className = '',
  bodyClassName = 'p-4',
}: {
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <div className={`bg-white border border-ink-200 rounded-xl overflow-hidden ${className}`}>
      {(title || actions) && (
        <div className="bg-ink-50 border-b border-ink-200 px-4 py-2.5 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-ink-500 uppercase tracking-wider">{title}</span>
          {actions}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

export function EmptyState({ icon, message, action }: { icon: string; message: string; action?: React.ReactNode }) {
  return (
    <div className="bg-white border border-dashed border-ink-300 rounded-xl p-10 text-center">
      <i className={`fas ${icon} text-3xl text-ink-300 mb-3`}></i>
      <p className="text-sm text-ink-500">{message}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex justify-center items-center py-16">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
    </div>
  );
}
